import React, { useEffect, useState } from 'react'
import Avatar from 'react-avatar';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux'
import { fetchCompetitorsThunk } from 'slices/competitionAnalysis/thunk';
import { AppDispatch, RootState } from 'store'
import igLogoPNG from "../../assets/images/brands/ig-logo.png"
import { getSocialAccountProfileURL } from 'utils/helpers';
import { Competitor } from 'types/competition-analysis';
import { kdf } from 'crypto-js';
import { toggleLimitExceededModal } from 'slices/ui/reducer';
import { addCompetitor } from 'services/api/competition-analysis';
import { toast } from 'react-toastify';
import { fetchMembershipThunk } from 'slices/membership/thunk';
import { fetchAgencyMetadataSuggestionsThunk } from 'slices/auth/thunk';

const Competitors = () => {
    const dispatch: AppDispatch = useDispatch();
    const [initialLoading, setInitialLoading] = useState(true);
    const competitorsData = useSelector((state: RootState) => state.competitionAnalysis.competitors);
    const competitors = (competitorsData?.items ?? []).filter((c) => c.via !== 'self');
    const loading = competitorsData?.loading ?? true
    const count = (competitorsData?.items ?? []).find((c) => c.via === 'self') ? (competitorsData?.count ?? 1) - 1 : (competitorsData?.count ?? 0);
    const activePlanFeatures = useSelector((state: RootState) => state.membershipState.membership?.data?.subscription_features);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [activating, setActivating] = useState("");
    useEffect(() => {
        setInitialLoading(true);
        dispatch(fetchCompetitorsThunk({ force: false })).finally(() => setInitialLoading(false));
    }, [])

    useEffect(() => {
        dispatch(fetchAgencyMetadataSuggestionsThunk());
    }, [])
    const activateCompetitor = async (competitor: Competitor) => {
        if (!selectedAgencyId) return;
        // if(competitors.length >= (activePlanFeatures?.max_competitors?.applicable_limit ?? 0)){
        //     return dispatch(toggleLimitExceededModal('max_competitors'));
        // }
        try {
            setActivating(competitor.social_account.uuid);
            await addCompetitor(selectedAgencyId, {
                handle: competitor.social_account.handle,
                platform: competitor.social_account.platform,
            });
            toast.success('Competitor activated successfully!');
            dispatch(fetchCompetitorsThunk({ force: true }));
            dispatch(fetchMembershipThunk({ force: true }));
        } catch (error: any) {
            if (error?.response?.data?.error_code === 'competitor_limit_reached') {
                return dispatch(toggleLimitExceededModal('max_competitors'));
            }
        } finally {
            setActivating("");
        }
    }

    return (
        <div className="card campaign-insights-card mt-4">
            <div className="card-body p-0 m-0">
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th className='text-start ps-4'>Platform</th>
                            <th className='text-start ps-4'>Handle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            !initialLoading && competitors.map((competitor, i) => {
                                return (
                                    <tr className={`border-bottom-1 px-3 competitor-row`} key={i}>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-start align-items-center gap-3 border-right-dashed td-content">
                                                    <span className={`status-badge ${competitor.status}`}></span>
                                                    {
                                                        competitor.social_account.profile_picture_url ? <div className={`competitor-logo ${competitor.status === 'inactive' ? 'inactive' : ''} bg-image bgc-white`} style={{ backgroundImage: `url(${competitor.social_account.profile_picture_url})` }} /> : <Avatar size='36' round={'4px'} name={competitor.social_account.name ?? competitor.social_account.handle} />
                                                    }
                                                    <span className={`user-name ${competitor.status === 'inactive' ? 'inactive' : ''}`}>{competitor.social_account.name ?? `@${competitor.social_account.handle}`}</span>
                                                    {
                                                        competitor.status === 'inactive' && <button onClick={() => activateCompetitor(competitor)} className='btn btn-gradient-outline btn-sm' style={{ height: '24px' }} disabled={activating === competitor.social_account.uuid}>{activating === competitor.social_account.uuid ? 'Activating...' : 'Activate'}</button>
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                        <td className={`ps-4 ${competitor.status === 'inactive' ? 'inactive' : ''}`}>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-start align-items-center gap-3 border-right-dashed td-content ps-3">
                                                    {
                                                        competitor.social_account.platform === 'ig' &&
                                                        <span className='d-flex align-items-center gap-2'><img src={igLogoPNG} alt="IG Logo" className='linked-posts-ig-logo' /> Instagram</span>
                                                    }
                                                    {
                                                        competitor.social_account.platform === 'yt' &&
                                                        <span className='d-flex align-items-center gap-2'><i className='ri-youtube-fill text-danger linked-posts-yt-logo mt-1'></i> Youtube</span>
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                        <td className={`ps-4 ${competitor.status === 'inactive' ? 'inactive' : ''}`}>
                                            <div className="py-3 d-flex justify-content-start align-items-center">
                                                <a href={getSocialAccountProfileURL(competitor.social_account.handle, 'ig')} target='_blank'>@{competitor.social_account.handle}</a>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        }
                        {
                            !initialLoading &&
                            <td colSpan={3}>
                                <div>
                                    Showing {competitors.length} of {competitors.length} Records
                                </div>
                            </td>
                        }
                        {
                            (loading || initialLoading) &&
                            Array.from({ length: 10 }).map((_, i) => {
                                return (
                                    <tr>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </table>
                {
                    !(loading || initialLoading) && competitors.length === 0 &&
                    <div className="d-flex w-100 justify-content-center align-items-center my-5">
                        <div className="length-zero my-5 d-flex flex-column gap-2">
                            <h3>No competitors added yet</h3>
                            <span>Add your first competitor to start tracking performance and insights.</span>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export default Competitors
